import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CodeSearchResult } from '../../../models/code-management.model';
import { IDropdowns } from '@core/models';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CodeManagementApiService } from '@core/services/code-management-api.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { PrimeNgModule } from '@shared/prime-ng/prime-ng.module';
import { DateService } from '@core/services/date.service';
import { PROGRAM_NAMES, PLAN_TYPES, DMEPOS_CATEGORIES } from '@core/constants/app.constants';
import { AutoComplete } from 'primeng/autocomplete';

@Component({
  selector: 'app-code-information',
  templateUrl: './code-information.component.html',
  styleUrls: ['./code-information.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PrimeNgModule
  ]
})
export class CodeInformationComponent implements OnInit, OnChanges, OnDestroy {
  @Input() mode: 'add' | 'edit' | 'view' = 'add';
  @Input() hcpcsCodeData: any;
  @Input() isExpandedViewEdit: boolean = false;
  @ViewChild('searchCodeInput') searchCodeInput!: AutoComplete;
  
  programOptions: IDropdowns[] = [];
  healthPlanOptions: IDropdowns[] = [];
  planTypeOptions: IDropdowns[] = [];
  dmeposCategoryOptions: IDropdowns[] = [];
  
  @Output() formChange = new EventEmitter<any>();
  @Output() searchCode = new EventEmitter<string>();
  @Output() programSelected = new EventEmitter<string>();
  @Output() healthPlanSelected = new EventEmitter<{program: string, healthPlan: string}>();
  @Output() codeSelected = new EventEmitter<boolean>();
  @Output() clinicalFormsLoaded = new EventEmitter<any[]>();
  @Output() modifiersLoaded = new EventEmitter<any[]>();
  
  codeInfoForm!: FormGroup;
  searchCodeValue = '';
  isExistingCode = false;
  isCodeNotFound = false;
  isHcpcsCodeExists = false;
  isPlanStartDateLocked = true;
  isPlanEndDateLocked = true;
  codeSearchResults: CodeSearchResult[] = [];
  selectedCodeResult: CodeSearchResult | null = null;
  codeSuggestions: string[] = [];
  private readonly destroy$ = new Subject<void>();
  private readonly requiredControlNames = [
    'cmsEffectiveStartDate',
    'program',
    'healthPlan',
    'planType',
    'planEffectiveStartDate'
  ];
  private readonly planDateControls = [
    'planEffectiveStartDate',
    'planEffectiveEndDate'
  ];

  constructor(
    private fb: FormBuilder,
    private codeManagementApiService: CodeManagementApiService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dateService: DateService
  ) {}

  ngOnInit(): void {
    this.buildCodeInformationForm();

    this.loadProgramOptions();
    this.loadHealthPlanOptions();

    if (this.mode === 'add') {
      this.applyRequiredValidators();
    }

    if (this.hcpcsCodeData) {
      this.populateFormWithHcpcsData(this.hcpcsCodeData);
      this.loadExpandedEditModeData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['mode'] || changes['isExpandedViewEdit']) && !changes['mode']?.firstChange) {
      
      if (this.codeInfoForm) {
        this.buildCodeInformationForm();
        
        if (this.hcpcsCodeData) {
          this.populateFormWithHcpcsData(this.hcpcsCodeData);
          this.loadExpandedEditModeData();
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadExpandedEditModeData(): void {
    if (this.isExpandedViewEdit && this.hcpcsCodeData?.healthPlan && this.hcpcsCodeData?.program) {
      this.isHcpcsCodeExists = this.hcpcsCodeData?.isHcpcsCodeExists ?? true;      
      this.isPlanStartDateLocked = true;
      this.isPlanEndDateLocked = true;
      
      this.loadPlanTypeOptions(this.hcpcsCodeData.healthPlan, this.hcpcsCodeData.program);
      
      if (this.isHcpcsCodeExists) {
        this.loadAndPreselectDmeposCategory(this.hcpcsCodeData.healthPlan, this.hcpcsCodeData.program);
        this.enableControls(['dmeposCategory']);
      } else {
        this.disableControls(['dmeposCategory']);
      }
    }
  }

  private buildCodeInformationForm(): void {
    const isViewMode = this.mode === 'view';
    const isExpandedEdit = this.isExpandedViewEdit && this.mode === 'edit';

    this.codeInfoForm = this.fb.group({
      searchCode: [{ value: '', disabled: isViewMode || isExpandedEdit }, [Validators.required, Validators.minLength(1), Validators.maxLength(20), Validators.pattern(/^[a-zA-Z0-9]+$/)]],
      code: [{ value: '', disabled: true }],
      shortDescription: [{ value: '', disabled: isViewMode }, [Validators.required, Validators.maxLength(100)]],
      cmsEffectiveStartDate: [{ value: null, disabled: true }],
      cmsEffectiveEndDate: [{ value: null, disabled: true }],
      program: [{ value: '', disabled: isViewMode || isExpandedEdit }],
      healthPlan: [{ value: '', disabled: isViewMode || isExpandedEdit }],
      planType: [{ value: '', disabled: isViewMode || isExpandedEdit }],
      planEffectiveStartDate: [{ value: null, disabled: isViewMode }, [Validators.required]],
      planEffectiveEndDate: [{ value: null, disabled: isViewMode }],
      dmeposCategory: [{ value: '', disabled: isViewMode }, [Validators.required]]
    });

    if (!isExpandedEdit) {
      this.codeInfoForm.get('searchCode')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          if (!value || value.length === 0) {
            this.clearSearchResults();
          } else if (this.selectedCodeResult && value !== this.selectedCodeResult.code) {
            this.clearSelectionOnly();
          }
        });

      this.codeInfoForm.get('program')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          if (value && !isViewMode) {
            this.enableControls(['healthPlan']);
            this.programSelected.emit(value);
          } else {
            this.resetProgramSelection();
          }
        });

      this.codeInfoForm.get('healthPlan')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          const programValue = this.codeInfoForm.get('program')?.value;
          if (value && programValue && !isViewMode) {
            this.enableControls(['planType']);
            this.loadPlanTypeOptions(value, programValue);
            this.loadDmeposCategoryOptions(value, programValue);

            this.healthPlanSelected.emit({ program: programValue, healthPlan: value });
          } else {
            this.resetPlanSelection();
          }
        });

      this.codeInfoForm.get('planType')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          if (value && !isViewMode) {
            this.enableControls(this.planDateControls);
            this.loadAssociationForSelection();
          } else {
            this.disableControls(this.planDateControls);
          }
        });
    }

    this.codeInfoForm.get('cmsEffectiveStartDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const endDateControl = this.codeInfoForm.get('cmsEffectiveEndDate');
        const endDateValue = endDateControl?.value;
        
        if (value) {
          endDateControl?.enable({ emitEvent: false });
          
          if (endDateValue && new Date(value) > new Date(endDateValue)) {
            endDateControl?.patchValue(null, { emitEvent: false });
          }
        } else {
          endDateControl?.disable({ emitEvent: false });
        }
        
        const planTypeSelected = this.codeInfoForm.get('planType')?.value;
        if (this.isPlanStartDateLocked && !isViewMode && !isExpandedEdit && planTypeSelected) {
          this.codeInfoForm.patchValue({
            planEffectiveStartDate: value
          }, { emitEvent: false });
          this.validatePlanEffectiveDates('planEffectiveStartDate');
        }
      });

    this.codeInfoForm.get('cmsEffectiveEndDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const planTypeSelected = this.codeInfoForm.get('planType')?.value;
        if (this.isPlanEndDateLocked && !isViewMode && !isExpandedEdit && planTypeSelected) {
          this.codeInfoForm.patchValue({
            planEffectiveEndDate: value
          }, { emitEvent: false });
          this.validatePlanEffectiveDates('planEffectiveEndDate');
        }
      });


    this.codeInfoForm.get('planEffectiveStartDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(planEffectiveStartDateValue => {
        this.validatePlanEffectiveDates('planEffectiveStartDate');
        const planEndDateControl = this.codeInfoForm.get('planEffectiveEndDate');
        if (planEffectiveStartDateValue && !this.isPlanEndDateLocked) {
          planEndDateControl?.enable({ emitEvent: false });
        } else if (!planEffectiveStartDateValue && !this.isPlanEndDateLocked) {
          planEndDateControl?.disable({ emitEvent: false });
        }
      });
    
    this.codeInfoForm.get('planEffectiveEndDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.validatePlanEffectiveDates('planEffectiveEndDate'));

    this.codeInfoForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFormChange();
      });
  }

  private emitFormChange(): void {
    const formValue = this.codeInfoForm.getRawValue();
    this.formChange.emit({
      ...formValue,
      tenantId: formValue.healthPlan, 
      isPlanStartDateLocked: this.isPlanStartDateLocked,
      isPlanEndDateLocked: this.isPlanEndDateLocked,
      isExistingCode: this.isExistingCode
    });
  }

  private clearSearchResults(): void {
    this.codeSearchResults = [];
    this.selectedCodeResult = null;
    this.isCodeNotFound = false;
    this.isExistingCode = false;
    this.isHcpcsCodeExists = false;
    
    this.codeInfoForm.patchValue({
      code: '',
      shortDescription: '',
      cmsEffectiveStartDate: null,
      cmsEffectiveEndDate: null,
      planEffectiveStartDate: null,
      planEffectiveEndDate: null,
      program: '',
      healthPlan: '',
      planType: '',
      dmeposCategory: ''
    });
    
    this.disableControls(['healthPlan', 'planType', 'planEffectiveStartDate', 'planEffectiveEndDate', 'dmeposCategory']);    
    this.enableControls(['cmsEffectiveStartDate']);
    this.clinicalFormsLoaded.emit([]);    
    this.modifiersLoaded.emit([]);
    this.codeSelected.emit(false);
  }

  private clearSelectionOnly(): void {
    this.selectedCodeResult = null;
    this.codeSearchResults = [];
    this.isCodeNotFound = false;
    this.isExistingCode = false;
    this.isHcpcsCodeExists = false;
    this.enableControls(['cmsEffectiveStartDate']);
    
    this.codeInfoForm.patchValue({
      code: '',
      shortDescription: '',
      cmsEffectiveStartDate: null,
      cmsEffectiveEndDate: null,
      planEffectiveStartDate: null,
      planEffectiveEndDate: null,
      program: '',
      healthPlan: '',
      planType: '',
      dmeposCategory: ''
    });
    
    this.disableControls(['healthPlan', 'planType', 'planEffectiveStartDate', 'planEffectiveEndDate', 'dmeposCategory']);    
    this.clinicalFormsLoaded.emit([]);    
    this.modifiersLoaded.emit([]);
    this.codeSelected.emit(false);
  }

  private enableControls(controlNames: string[]): void {
    controlNames.forEach(controlName => this.codeInfoForm.get(controlName)?.enable());
  }

  private disableControls(controlNames: string[]): void {
    controlNames.forEach(controlName => this.codeInfoForm.get(controlName)?.disable());
  }

  private resetProgramSelection(): void {
    this.codeInfoForm.patchValue({
      healthPlan: ''
    });
    this.disableControls(['healthPlan']);
    this.resetPlanSelection();
  }

  private resetPlanSelection(): void {
    this.codeInfoForm.patchValue({
      planType: '',
      dmeposCategory: ''
    });
    this.disableControls(['planType', 'dmeposCategory', ...this.planDateControls]);
  }

  private applyRequiredValidators(): void {
    this.requiredControlNames.forEach(controlName => {
      const control = this.codeInfoForm.get(controlName);
      if (!control) {
        return;
      }

      control.setValidators([Validators.required]);
      control.updateValueAndValidity();
    });
  }

  private populateFormWithHcpcsData(data: any): void {
    if (!data) {
      return;
    }

    // In view mode or expanded edit mode, always set searchCodeValue to show all fields
    this.searchCodeValue = (this.mode === 'view' || this.isExpandedViewEdit) ? (data.code || data.searchCode || 'view') : (data.searchCode || data.code || '');
    this.isExistingCode = !!data.code;

    if (data.code && data.shortDescription) {
      this.codeSearchResults = [{
        found: true,
        hcpcsId: data.hcpcsId || 0,
        code: data.code,
        shortDescription: data.shortDescription,
        hasAssociation: false
      }];
      this.selectedCodeResult = this.codeSearchResults[0];
    }

    this.codeInfoForm.patchValue({
      searchCode: data.searchCode || data.code,
      code: data.code,
      shortDescription: data.shortDescription,
      cmsEffectiveStartDate: data.cmsEffectiveStartDate,
      cmsEffectiveEndDate: data.cmsEffectiveEndDate,
      program: data.program,
      healthPlan: data.healthPlan,
      planType: data.planType,
      planEffectiveStartDate: data.planEffectiveStartDate,
      planEffectiveEndDate: data.planEffectiveEndDate,
      dmeposCategory: data.dmeposCategory === '-' ? '' : data.dmeposCategory
    }, { emitEvent: false });

    if (data.isPlanStartDateLocked !== undefined) {
      this.isPlanStartDateLocked = data.isPlanStartDateLocked;
    }
    if (data.isPlanEndDateLocked !== undefined) {
      this.isPlanEndDateLocked = data.isPlanEndDateLocked;
    }

    if (this.searchCodeValue) {
      this.applyRequiredValidators();
    }
    
    this.validatePlanEffectiveDates('planEffectiveStartDate');
    
    this.emitFormChange();
  }
  
  private validatePlanEffectiveDates(editedField: 'planEffectiveStartDate' | 'planEffectiveEndDate'): void {
    const planStartDateControl = this.codeInfoForm.get('planEffectiveStartDate');
    const planEndDateControl = this.codeInfoForm.get('planEffectiveEndDate');
    
    const isPlanStartDateGreaterThanEndDate = planStartDateControl?.value && planEndDateControl?.value && 
                      new Date(planStartDateControl.value) > new Date(planEndDateControl.value);
    
    if (isPlanStartDateGreaterThanEndDate) {
      planStartDateControl?.setErrors({ dateRangeInvalid: true });
    } else {
      if (planStartDateControl?.hasError('dateRangeInvalid')) planStartDateControl.setErrors(null);
    }
  }

  private loadProgramOptions(): void {
    this.codeManagementApiService.getAllProducts().subscribe({
      next: (products) => {
        this.programOptions = products.map(product => ({
          label: product.shortName,
          value: product.productId
        }));
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load programs'
        });
      }
    });
  }

  private loadHealthPlanOptions(): void {
    this.codeManagementApiService.getTenants().subscribe({
      next: (tenants) => {
        this.healthPlanOptions = tenants.map(tenant => ({
          label: tenant.name,
          value: tenant.tenantId
        }));
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load health plans'
        });
      }
    });
  }

  private loadPlanTypeOptions(tenantId: string, productId: string): void {
    this.codeManagementApiService.getPlans(tenantId, productId).subscribe({
      next: (plans) => {
        const programName = this.programOptions.find(p => p.value === productId)?.label;
        
        let filteredPlans = plans;
        if (programName === PROGRAM_NAMES.DMEPOS) {
          filteredPlans = plans.filter(plan => 
            plan.overrideApplicationKey === PLAN_TYPES.MEDICARE_ADVANTAGE
          );
        }
        
        this.planTypeOptions = filteredPlans.map(plan => ({
          label: plan.overrideApplicationKey,
          value: plan.overrideTypeId
        }));
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load plan types'
        });
      }
    });
  }

  private loadDmeposCategoryOptions(tenantId: string, productId: string, onSuccess?: () => void): void {
    this.codeManagementApiService.getDmeposCategories(tenantId, productId).subscribe({
      next: (categories) => {
        this.dmeposCategoryOptions = categories
          .filter(category => category.description.toLowerCase() !== DMEPOS_CATEGORIES.INCOMPLETE)
          .map(category => ({
            label: category.description,
            value: category.categoryId
          }));
        
        if (onSuccess) {
          onSuccess();
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load DMEPOS categories'
        });
      }
    });
  }

  private loadAndPreselectDmeposCategory(tenantId: string, productId: string): void {
    this.loadDmeposCategoryOptions(tenantId, productId, () => {
      if (this.hcpcsCodeData?.dmeposCategory) {
        this.preselectDmeposCategory(this.hcpcsCodeData.dmeposCategory);
      }
      this.codeInfoForm.get('dmeposCategory')?.markAsTouched();
    });
  }

  private preselectDmeposCategory(categoryDescription: string): void {
    const matchedDmeposCategory = this.dmeposCategoryOptions.find(
      option => option.label.toLowerCase() === categoryDescription.toLowerCase()
    );
    
    if (matchedDmeposCategory) {
      this.codeInfoForm.patchValue({
        dmeposCategory: matchedDmeposCategory.value
      }, { emitEvent: false });
    }
  }

  onSearchCodeQuery(event: { query: string }): void {
    const query = event.query;

    if (this.selectedCodeResult && query !== this.selectedCodeResult.code) {
      this.resetFormForNewSearch();
    }

    if (query && query.length >= 1) {
      this.codeManagementApiService.searchCodes(query).subscribe({
        next: (results: any[]) => {
          const mappedResults = (results || []).map((result: any) => ({
            hcpcsId: result.hcpcsId,
            code: result.hcpcsCode,
            shortDescription: '',
            cmsEffectiveStartDate: '',
            cmsEffectiveEndDate: undefined,
            found: true,
            hasAssociation: false
          }));

          this.codeSearchResults = mappedResults;
          this.codeSuggestions = mappedResults.map(result => result.code || '');
          this.isCodeNotFound = mappedResults.length === 0;

          if (mappedResults.length === 0) {
            this.applyNoSearchResults(query);
          }
        },
        error: () => {
          this.applyNoSearchResults(query);
        }
      });
    } else {
      this.codeSearchResults = [];
      this.codeSuggestions = [];
    }
  }

  private resetFormForNewSearch(): void {
    this.selectedCodeResult = null;
    this.isExistingCode = false;
    this.isHcpcsCodeExists = false;

    this.codeInfoForm.patchValue({
      searchCode: '',
      code: '',
      shortDescription: '',
      cmsEffectiveStartDate: null,
      cmsEffectiveEndDate: null,
      planEffectiveStartDate: null,
      planEffectiveEndDate: null,
      program: '',
      healthPlan: '',
      planType: '',
      dmeposCategory: ''
    });

    this.codeInfoForm.markAsUntouched();
    this.codeInfoForm.markAsPristine();

    this.disableControls(['healthPlan', 'planType', 'planEffectiveStartDate', 'planEffectiveEndDate', 'dmeposCategory']);    
    this.clinicalFormsLoaded.emit([]);
    this.modifiersLoaded.emit([]);
  }

  private applyNoSearchResults(query: string): void{
    this.codeSearchResults = [];
    this.codeSuggestions = [];
    this.isCodeNotFound = true;
    this.searchCodeValue = query;

    this.codeInfoForm.patchValue({
      code: query
    });
    this.selectedCodeResult = null;
    this.isExistingCode = false;
    this.isPlanStartDateLocked = true;
    this.isPlanEndDateLocked = true;
    this.enableControls(['cmsEffectiveStartDate']);
  }

  onAutocompleteCodeSelected(event: { value: string }): void {
    const selectedCode = event.value;
    if (selectedCode) {
      const result = this.codeSearchResults.find(searchResult => searchResult.code === selectedCode);
      if (result) {
        this.selectSearchResult(result);
      }
    }
  }

  onSearchResultClick(result: CodeSearchResult): void {
    this.selectSearchResult(result);
  }

  private selectSearchResult(result: CodeSearchResult): void {
    this.selectedCodeResult = result;
    this.isCodeNotFound = false;
    this.searchCodeValue = result.code || '';
    this.isExistingCode = !!result.hcpcsId;

    this.codeInfoForm.patchValue({
      searchCode: result.code || '',
      program: '',
      healthPlan: '',
      planType: '',
      dmeposCategory: ''
    });
    
    this.disableControls(['healthPlan', 'planType', 'planEffectiveStartDate', 'planEffectiveEndDate', 'dmeposCategory']);
    this.codeSearchResults = [result];
    this.codeSuggestions = [];
    this.searchCode.emit(result.code || '');    
    this.codeSelected.emit(false);

    this.applyRequiredValidators();

    if (result.hcpcsId) {
      this.codeManagementApiService.getCodeDetails(result.hcpcsId).subscribe({
        next: (details) => {
          const cmsStartDate = details.cmsEffectiveStartDate ? this.dateService.parseDate(details.cmsEffectiveStartDate) : null;
          const cmsEndDate = details.cmsEffectiveEndDate ? this.dateService.parseDate(details.cmsEffectiveEndDate) : null;

          this.selectedCodeResult = {
            ...result,
            shortDescription: details.shortDescription,
            cmsEffectiveStartDate: details.cmsEffectiveStartDate,
            cmsEffectiveEndDate: details.cmsEffectiveEndDate
          };

          this.codeSearchResults = [this.selectedCodeResult];

          this.codeInfoForm.patchValue({
            searchCode: result.code,
            code: result.code,
            shortDescription: details.shortDescription,
            cmsEffectiveStartDate: cmsStartDate,
            cmsEffectiveEndDate: cmsEndDate,
            planEffectiveStartDate: null,
            planEffectiveEndDate: null,
            program: '',
            healthPlan: '',
            planType: '',
            dmeposCategory: ''
          });

          this.disableControls(['cmsEffectiveStartDate', 'cmsEffectiveEndDate', ...this.planDateControls, 'dmeposCategory']);

          this.isPlanStartDateLocked = true;
          this.isPlanEndDateLocked = true;
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load code details'
          });
        }
      });
    }
  }

  private loadCategoryForCode(hcpcsId: string, tenantId: string): void {
    this.codeManagementApiService.getCategoryForHcpcs(hcpcsId, tenantId).subscribe({
      next: (categoryId) => {        
        if (categoryId) {
          this.codeInfoForm.patchValue({
            dmeposCategory: categoryId
          }, { emitEvent: false });
        } else {
          this.codeInfoForm.patchValue({
            dmeposCategory: ''
          }, { emitEvent: false });
        }
        this.codeInfoForm.get('dmeposCategory')?.updateValueAndValidity();
      },
      error: () => {
        this.codeInfoForm.patchValue({
          dmeposCategory: ''
        }, { emitEvent: false });
      }
    });
  }

  private loadAssociationForSelection(): void {
    const hcpcsId = this.selectedCodeResult?.hcpcsId;
    const productId = this.codeInfoForm.get('program')?.value;
    const tenantId = this.codeInfoForm.get('healthPlan')?.value;
    const overrideTypeId = this.codeInfoForm.get('planType')?.value;

    if (!hcpcsId) {
      if (productId && tenantId && overrideTypeId) {
        this.syncPlanDatesToCms();
      }
      return;
    }

    if (!productId || !tenantId || !overrideTypeId) {
      return;
    }

    this.codeManagementApiService.getCodeAssociations(hcpcsId, productId, tenantId, overrideTypeId).subscribe({
      next: (associations) => {
        this.isHcpcsCodeExists = associations.isHcpcsCodeExists || false;

        if (this.isHcpcsCodeExists) {
          this.enableControls(['dmeposCategory']);
          this.codeInfoForm.get('dmeposCategory')?.markAsTouched();
          if (hcpcsId && tenantId) {
            this.loadCategoryForCode(hcpcsId, tenantId);
          }
        } else {
          this.disableControls(['dmeposCategory']);
          this.codeInfoForm.patchValue({ dmeposCategory: '' }, { emitEvent: false });
        }

        if (associations.hasAssociation) {
          const planStartDate = associations.planEffectiveStartDate 
            ? this.dateService.parseDate(associations.planEffectiveStartDate.toString()) 
            : null;
          const planEndDate = associations.planEffectiveEndDate 
            ? this.dateService.parseDate(associations.planEffectiveEndDate.toString()) 
            : null;

          this.codeInfoForm.patchValue({
            planEffectiveStartDate: planStartDate,
            planEffectiveEndDate: planEndDate
          }, { emitEvent: false });

          if (associations.hcpcsClinicalForms && associations.hcpcsClinicalForms.length > 0) {
            const clinicalForms = associations.hcpcsClinicalForms.map((form: any) => ({
              id: form.hcpcsToFormId,
              associatedFormId: form.formIoId,
              associatedFormTitle: form.formIoTitle,
              sharedForm: form.isSharedForm ? 'Yes' : 'No',
              formEffectiveStartDate: this.dateService.parseDate(form.effectiveStartDate.toString()),
              isNewClinicalForm: false,
              isEditing: false,
              isFormDateLocked: true
            }));
            this.clinicalFormsLoaded.emit(clinicalForms);
          } else {
            this.clinicalFormsLoaded.emit([]);
          }

          if (associations.hcpcsModifiers && associations.hcpcsModifiers.length > 0) {
            this.modifiersLoaded.emit(associations.hcpcsModifiers);
          } else {
            this.modifiersLoaded.emit([]);
          }

          this.confirmationService.confirm({
            header: 'Existing Association Detected',
            message: '<b>This association already exists with the corresponding information displayed</b>.<br>Would you like to proceed and make further modifications or cancel to clear the loaded data and start fresh?',
            icon: 'none',
            acceptLabel: 'Proceed',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-primary',
            rejectButtonStyleClass: 'p-button-secondary',
            accept: () => {
              this.isExistingCode = true;
              this.emitFormChange();
            },
            reject: () => {
              this.resetFormForNewSearch();
            }
          });
        } else {
          this.syncPlanDatesToCms();
          this.clinicalFormsLoaded.emit([]);
          this.modifiersLoaded.emit([]);
        }
      },
      error: () => {
        this.syncPlanDatesToCms();
      }
    });
  }

  preselectPlanType(planTypeId: string): void {
    if (planTypeId && !this.isExistingCode) {
      this.codeInfoForm.patchValue({
        planType: planTypeId
      });
    }
  }

  clearSearchCode(): void {
    this.codeInfoForm.patchValue({
      searchCode: ''
    });
    this.clearSearchResults();
  }

  togglePlanStartDateLock(): void {
    if (this.mode === 'view') return;
    
    this.isPlanStartDateLocked = !this.isPlanStartDateLocked;
    
    if (this.isPlanStartDateLocked) {
      const cmsStartDate = this.codeInfoForm.get('cmsEffectiveStartDate')?.value;
      this.codeInfoForm.patchValue({
        planEffectiveStartDate: cmsStartDate
      });
    } else {
      this.messageService.add({
        severity: 'info',
        summary: 'Effective Dates Unlocked',
        detail: 'You can proceed with editing'
      });
    }
  }

  togglePlanEndDateLock(): void {
    if (this.mode === 'view') return;
    
    this.isPlanEndDateLocked = !this.isPlanEndDateLocked;
    
    if (this.isPlanEndDateLocked) {
      const cmsEndDate = this.codeInfoForm.get('cmsEffectiveEndDate')?.value;
      this.codeInfoForm.patchValue({
        planEffectiveEndDate: cmsEndDate
      });
    } else {
      this.messageService.add({
        severity: 'info',
        summary: 'Effective Dates Unlocked',
        detail: 'You can proceed with editing'
      });
    }
  }

  private syncPlanDatesToCms(): void {
    const cmsStartDate = this.codeInfoForm.get('cmsEffectiveStartDate')?.value;
    const cmsEndDate = this.codeInfoForm.get('cmsEffectiveEndDate')?.value;

    this.codeInfoForm.patchValue({
      planEffectiveStartDate: cmsStartDate,
      planEffectiveEndDate: cmsEndDate
    });
  }

  get isValid(): boolean {
    const formValue = this.codeInfoForm.getRawValue();
    
    if (this.mode !== 'view') {
      if (!formValue.searchCode || !formValue.shortDescription) {
        return false;
      }
      
      if (!formValue.cmsEffectiveStartDate) {
        return false;
      }
      
      if (!formValue.program || !formValue.healthPlan || !formValue.planType) {
        return false;
      }
      
      if (!formValue.planEffectiveStartDate) {
        return false;
      }
    }
    
    return true;
  }

  getFormValue(): any {
    return {
      ...this.codeInfoForm.getRawValue(),
      isPlanStartDateLocked: this.isPlanStartDateLocked,
      isPlanEndDateLocked: this.isPlanEndDateLocked
    };
  }

  restrictToAlphanumeric(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;
    const char = String.fromCharCode(charCode);
    
    const alphanumericPattern = /^[a-zA-Z0-9]$/;
    
    if (!alphanumericPattern.test(char)) {
      event.preventDefault();
      return;
    }
  }

  focusSearchInput(): void {
    setTimeout(() => {
      if (this.searchCodeInput?.inputEL) {
        this.searchCodeInput.inputEL.nativeElement.focus();
      }
    });
  }

}
